/**
 * ProjectPicker — combobox projet (parité simplifiée BrokerPicker / CompanyPicker).
 *
 * Consommé par CreateIntakeLinkDialog (Chunk 9.N.3.A) pour rattacher un nouveau
 * lien d'intake à un projet broker existant. Pattern minimal :
 *   - lazy load à la frappe (≥ 2 chars) pour éviter de charger tous les projets
 *     d'emblée (peut être > 200) ;
 *   - sélection unique, valeur = `project_id` UUID ;
 *   - bouton « Changer » réouvre la recherche après sélection ;
 *   - aucun bouton de désassignation (un intake link DOIT être attaché à un
 *     projet — le formulaire CreateIntakeLinkDialog rejette la submission si
 *     `value` est `undefined`).
 *
 * Réutilisation `useProjects` existant ([apps/web/src/lib/queries/projects.ts]).
 * Filtre `q` accepté par GET /v1/projects (search full-text title/reference_code).
 *
 * a11y :
 *   - input search avec `aria-expanded`, `autoComplete="off"`
 *   - liste résultats `<ul>` avec `<button>` items (pas `<div onClick>`)
 *   - focus-visible:ring-2 brand-500
 *   - touch-manipulation sur boutons (≥ 44pt mobile)
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useProject, useProjects } from "../lib/queries/projects";

type Props = {
  value: string | undefined;
  onChange: (projectId: string | undefined) => void;
  id?: string;
  disabled?: boolean;
  companyId?: string;
  /** Active une popup compacte (default false). */
  ariaLabelledBy?: string;
};

export function ProjectPicker({ value, onChange, id, disabled, companyId, ariaLabelledBy }: Props) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const selected = useProject(value);

  const enabled = open && q.trim().length >= 2;
  const results = useProjects(
    { q: enabled ? q : undefined, company_id: companyId, limit: 20 },
    { enabled },
  );

  useEffect(() => {
    if (!value) setQ("");
  }, [value]);

  if (value && selected.data) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 space-y-0.5">
            <div className="truncate text-sm font-semibold text-slate-900">
              {selected.data.title}
            </div>
            {selected.data.company_legal_name ? (
              <div className="truncate text-xs text-slate-600">
                <span className="text-slate-500">{t("projects.picker.company_label")}</span>{" "}
                <span className="font-medium">{selected.data.company_legal_name}</span>
              </div>
            ) : null}
            <div className="truncate text-xs text-slate-500">
              <span>{t("projects.picker.reference_label")}</span>{" "}
              <span className="font-mono" translate="no">
                {selected.data.reference_code}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="text-brand-700 hover:bg-brand-50 focus-visible:ring-brand-500 shrink-0 touch-manipulation rounded-md px-2 py-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            onClick={() => {
              onChange(undefined);
              setOpen(true);
            }}
            disabled={disabled}
          >
            {t("common.change")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <input
        id={id}
        type="search"
        className="input w-full"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={t("projects.search_placeholder", {
          defaultValue: "Rechercher un projet…",
        })}
        disabled={disabled}
        autoComplete="off"
        aria-expanded={open}
        aria-labelledby={ariaLabelledBy}
      />

      {open && enabled && (
        <div className="mt-1 max-h-60 overflow-auto rounded-lg border border-slate-200 bg-white shadow-sm">
          {results.isError ? (
            <p role="alert" className="p-3 text-sm text-red-700">
              {t("projects.picker.load_error")}
            </p>
          ) : results.isLoading && !results.data ? (
            <div className="p-3 text-sm text-slate-400">{t("app.loading")}</div>
          ) : results.data && results.data.items.length === 0 ? (
            <div className="p-3 text-sm text-slate-500">
              {t("projects.empty", { defaultValue: "Aucun projet correspondant." })}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {results.data?.items.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className="focus-visible:bg-brand-50 focus-visible:ring-brand-500 block w-full touch-manipulation px-3 py-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-[-2px]"
                    onClick={() => {
                      onChange(p.id);
                      setOpen(false);
                    }}
                  >
                    <div className="truncate text-sm font-semibold text-slate-900">{p.title}</div>
                    {p.company_legal_name ? (
                      <div className="truncate text-xs text-slate-600">
                        <span className="text-slate-500">{t("projects.picker.company_label")}</span>{" "}
                        <span className="font-medium">{p.company_legal_name}</span>
                      </div>
                    ) : null}
                    <div className="truncate text-xs text-slate-500">
                      <span>{t("projects.picker.reference_label")}</span>{" "}
                      <span className="font-mono" translate="no">
                        {p.reference_code}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {open && !enabled && (
        <p className="mt-1 text-xs text-slate-400">{t("common.search_min_chars", { count: 2 })}</p>
      )}
    </div>
  );
}
